/*
 *  Bundle Upsells
 *    Scripts for bundle upsells
 *
 *  Version:
 *    1.0.0 - 2022/04/26
 */

import {
  CartAdd,
  cartAdd,
  getCountOfVariantInCart,
  printMoney,
} from '@process-creative/slate-theme-tools';

import { ATTR_DISABLED } from '../../tools/constants/attributes';
import { CLASS_DISABLED } from '../../tools/constants/classes';
import { t } from '../../tools/utilities/string-tools';
import { Variant } from '../../types/variant';

const SELECTOR_UPSELL_FORM_CONTAINER = '[data-bundle-upsell]';
const SELECTOR_UPSELL_FORM = '[data-bundle-upsell-form]';
const SELECTOR_SELECT = '[data-bundle-upsell-select]';
const SELECTOR_PRICE = '[data-bundle-upsell-price]';
const SELECTOR_ATC = '[data-upsell-add]';
const SELECTOR_ELEMENT_DATA = '[data-variant-selector-data]';

document
  .querySelectorAll(SELECTOR_UPSELL_FORM_CONTAINER)
  .forEach((container) => {
    const form = container.querySelector<HTMLFormElement>(SELECTOR_UPSELL_FORM);
    const select = container.querySelector<HTMLSelectElement>(SELECTOR_SELECT);
    const button = container.querySelector<HTMLButtonElement>(SELECTOR_ATC);
    const elementData = container.querySelector<HTMLElement>(
      SELECTOR_ELEMENT_DATA
    );
    let variantID: number;

    if (!form || !select || !button || !elementData) return;

    const data = JSON.parse(elementData.innerHTML);
    const variants: Variant[] = data.variants;
    const tags: string[] = data.tags;

    /**
     * Updates variant ID and button text
     */
    const onVariantSelect = () => {
      variantID = Number(select.value);
      buttonUpdate();
    };

    /**
     * Disables button and updates text
     */
    const btnAllInCart = () => {
      button.classList.add(CLASS_DISABLED);
      button.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
      button.innerHTML = t('products.product.all_in_cart');
    };

    /**
     * Enables button and updates text
     */
    const btnEnable = (price: number) => {
      const innerHtml = `${t('products.product.quick_add')} - ${printMoney(
        price
      )}`;
      button.removeAttribute(ATTR_DISABLED);
      button.classList.remove(CLASS_DISABLED);
      button.innerHTML = innerHtml;
    };

    /**
     * Disables button and updates text
     */
    const btnDisable = (outOfStock: boolean) => {
      button.classList.add(CLASS_DISABLED);
      button.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
      if (outOfStock) {
        button.innerHTML = t('products.product.sold_out');
      } else {
        button.innerHTML = t('products.product.quick_add');
      }
    };

    /**
     * Returns variant object based on variant id from select
     */
    const getVariant = () => {
      return variants.find((variant) => variant.id === variantID)!;
    };

    /**
     * Adds product to cart, then updates button
     * @param evt event
     */
    const addToCart = async (evt: Event) => {
      evt.preventDefault();
      button.disabled = true;
      button.innerHTML = t('products.product.adding');
      const item = Array.from(new FormData(form)).reduce((x, y) => {
        return { ...x, [y[0]]: y[1].toString() };
      }, {} as CartAdd);
      if (tags.includes('bundle')) {
        item.properties = {
          bundle: data.title.split('(')[1].replace(')', ''),
        };
      }

      try {
        item.quantity = 1;
        await cartAdd({ items: [item] });
      } catch (err) {
        console.error(err);
        // eslint-disable-next-line no-alert
        alert(err);
      }

      buttonUpdate();
    };

    /**
     * Updates button text based on availability and inventory policy
     */
    const buttonUpdate = () => {
      const variant = getVariant();
      if (!variant) {
        btnDisable(false);
        return;
      }
      const { price, inventory_management, inventory_policy, available } =
        variant;
      let { inventory_quantity } = variant;
      if (!inventory_quantity) inventory_quantity = 0;
      const inCart = getCountOfVariantInCart(variant.id);

      if (available && inventory_quantity > 0) {
        if (
          inventory_policy !== 'continue' &&
          inventory_management != null &&
          inventory_quantity - inCart <= 0
        ) {
          btnAllInCart();
        } else {
          btnEnable(price);
        }
      } else {
        btnDisable(true);
      }
    };

    // Event Listeners
    select.addEventListener('input', () => onVariantSelect());
    button.addEventListener('click', (evt) => addToCart(evt));
  });
